/*
 * Sticky "Add to cart / Buy now" bar with a "Choose your size" sheet (phones).
 * Markup and styles: snippets/epg-sticky-buy.liquid.
 *
 * This never adds to cart or starts checkout itself. It selects the size in the page's
 * own size picker, then presses the product form's real button, so the theme's cart
 * drawer, variant logic, Shopify's dynamic checkout and Razorpay Magic Checkout all run
 * exactly as they do when a customer taps those buttons directly.
 */
if (!customElements.get('epg-sticky-buy')) {
  class EpgStickyBuy extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      // Combo products skip the standard buy buttons, so there is nothing to press.
      if (!this.getForm()) {
        this.hidden = true;
        return;
      }

      this.optionName = this.dataset.optionName || '';
      this.sheet = this.querySelector('[data-sheet]');
      this.confirmBtn = this.querySelector('[data-confirm]');
      this.status = this.querySelector('[data-status]');
      this.statusText = this.querySelector('[data-status-text]');
      this.sizes = Array.from(this.querySelectorAll('[data-size]'));
      this.pending = null;
      this.selected = null;
      this.lastTrigger = null;

      this.querySelectorAll('[data-action]').forEach((btn) => {
        btn.addEventListener('click', () => this.onAction(btn.dataset.action, btn));
      });
      this.querySelectorAll('[data-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
      this.sizes.forEach((btn) => btn.addEventListener('click', () => this.pick(btn)));
      if (this.confirmBtn) this.confirmBtn.addEventListener('click', () => this.confirm());

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen()) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('epg-sb-lock');
    }

    // Looked up fresh every time: a size change re-renders parts of the product info.
    getForm() {
      return document.getElementById(this.dataset.formId);
    }

    scope() {
      const form = this.getForm();
      return (form && form.closest('.productView')) || document;
    }

    realButton(action) {
      const form = this.getForm();
      if (!form) return null;
      if (action === 'buy') {
        return form.querySelector('.shopify-payment-button__button') ||
          form.querySelector('.shopify-payment-button button');
      }
      return form.querySelector('[data-btn-addToCart]') || form.querySelector('button[name="add"]');
    }

    radios() {
      return Array.from(this.scope().querySelectorAll('input.product-form__radio'))
        .filter((radio) => radio.name === this.optionName);
    }

    currentValue() {
      const checked = this.radios().find((radio) => radio.checked);
      return checked ? checked.value : null;
    }

    onAction(action, trigger) {
      this.pending = action;
      this.lastTrigger = trigger;
      // Single-variant products have nothing to choose.
      if (!this.sheet) {
        this.fire(action);
        return;
      }
      this.open();
    }

    open() {
      this.confirmBtn.textContent = this.pending === 'buy' ? this.dataset.labelBuy : this.dataset.labelCart;

      // Start from the size already chosen on the page, if it is still in stock.
      const current = this.currentValue();
      const match = this.sizes.find((btn) => btn.dataset.value === current && !btn.disabled);
      this.setSelected(match || null);

      this.classList.add('is-open');
      this.sheet.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('epg-sb-lock');

      const focusTarget = match || this.sizes.find((btn) => !btn.disabled) || this.confirmBtn;
      focusTarget.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen()) return;
      this.classList.remove('is-open');
      this.sheet.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('epg-sb-lock');
      if (this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
    }

    isOpen() {
      return this.classList.contains('is-open');
    }

    setSelected(btn) {
      this.selected = btn;
      this.sizes.forEach((size) => size.setAttribute('aria-pressed', size === btn ? 'true' : 'false'));
      this.confirmBtn.disabled = !btn;
      // "Your size is selected: M (38)" under the pills (also read out by screen readers).
      if (this.status && this.statusText) {
        this.statusText.textContent = btn ? `${this.status.dataset.text} ${btn.dataset.value}` : '';
        this.status.classList.toggle('is-shown', Boolean(btn));
      }
    }

    pick(btn) {
      if (btn.disabled) return;
      this.setSelected(btn);
      // Drive the page's own size picker so the price, stock and the form's variant follow.
      const radio = this.radios().find((r) => r.value === btn.dataset.value);
      if (radio && !radio.checked) radio.click();
    }

    confirm() {
      if (!this.selected) return;
      // Press the real button inside this same tap. Dynamic checkout and wallet buttons
      // need the customer's gesture, which a delayed call would lose.
      this.fire(this.pending);
      this.close();
    }

    fire(action) {
      const btn = this.realButton(action);
      if (btn && !btn.disabled) {
        btn.click();
        return;
      }
      // Nothing pressable (e.g. the checkout button has not rendered yet): show the real one.
      const target = btn || this.getForm();
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('epg-sticky-buy', EpgStickyBuy);
}
